import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import Step1PersonalDetails from "./Step1PersonalDetails";
import Step2AbstractDetails from "./Step2AbstractDetails";
import Step3AccompanyingDetails from "./Step3AccompanyingDetails";
import Step4PaymentSelection from "./Step4PaymentSelection";

import Navbar from "./Navbar";
import Footer from "./Footer";
import "./RegistrationForm.css";

const API_BASE_URL = "https://api.rescons.in";
const steps = ["Delegate Details", "Abstract Details", "Accompanying Person", "Payment"];

const RegistrationForm = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [abstractsError, setAbstractsError] = useState(false);
  const [userPayments, setUserPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // Remove hasUnsavedChanges state - no longer needed

  const [formData, setFormData] = useState({
    category1: "",
    title: "",
    givenName: "",
    familyName: "",
    fullName: "",
    email: "",
    phone: "",
    designation: "",
    address: "",
    country: "",
    zipcode: "",
    dietaryPreferenceAuthor: "",
    accompanyingPersons: [],
    selectedCategory: "",
    selectedCategoryDetails: {
      totalAmount: 0,
      categories: [],
    },
    abstractSubmissions: [],
  });

  useEffect(() => {
    const storedUid = sessionStorage.getItem("uid");
    if (!storedUid) {
      window.location.href = `/stis2025/login-signup?redirect=/stis2025/registration-form`;
    } else {
      fetchUserInfo(storedUid);
      fetchUserPayments(storedUid);
    }
  }, []);

  // Remove the problematic auto-save useEffect that was causing infinite loops

  const fetchUserInfo = async (uid) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/user-info/${uid}`);
      if (response.data) {
        // Clean the data received from backend
        const cleanedData = { ...response.data };
        
        // Ensure array fields are properly handled
        if (!Array.isArray(cleanedData.accompanyingPersons)) {
          cleanedData.accompanyingPersons = [];
        }
        if (!Array.isArray(cleanedData.abstractSubmissions)) {
          cleanedData.abstractSubmissions = [];
        }
        if (!Array.isArray(cleanedData.payments)) {
          cleanedData.payments = [];
        }
        
        console.log("📋 Fetched user data:", JSON.stringify(cleanedData, null, 2));
        setFormData(prev => ({ ...prev, ...cleanedData }));
        // Clear unsaved changes when data is loaded
      }
    } catch (error) {
      console.error("❌ Error fetching user info:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateUserInfo = async (updatedFields) => {
    try {
      const storedUid = sessionStorage.getItem("uid");
      if (!storedUid) return;
      
      setIsSaving(true);
      
      // Clean the data before sending to backend
      const cleanedData = { ...updatedFields };
      
      // Ensure array fields are properly handled
      if (!Array.isArray(cleanedData.accompanyingPersons)) {
        cleanedData.accompanyingPersons = [];
      }
      if (!Array.isArray(cleanedData.abstractSubmissions)) {
        cleanedData.abstractSubmissions = [];
      }
      if (!Array.isArray(cleanedData.payments)) {
        cleanedData.payments = [];
      }
      
      console.log("🔄 Frontend sending update data:", JSON.stringify(cleanedData, null, 2));
      console.log("🆔 UID:", storedUid);
      
      // Debug: Check if accompanyingPersons is in the data
      if (cleanedData.accompanyingPersons) {
        console.log("📋 Accompanying Persons data being sent:", cleanedData.accompanyingPersons);
      }
      
      // Debug: Check if category1 is in the data
      if (cleanedData.category1) {
        console.log("📋 Category1 data being sent:", cleanedData.category1);
      } else {
        console.log("⚠️ Category1 is missing from update data");
      }
      
      await axios.put(`${API_BASE_URL}/user-info/update/${storedUid}`, cleanedData);
      console.log("✅ Frontend update request completed");
      // Clear unsaved changes flag
    } catch (error) {
      console.error("❌ Error updating user info:", error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const fetchUserPayments = async (uid) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/user-info/${uid}`);
      if (response.data && response.data.payments) {
        setUserPayments(response.data.payments);
      }
    } catch (error) {
      console.error("Error fetching user payments:", error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const nextStep = async () => {
    console.log("🔄 nextStep called, current step:", step);
    console.log("📋 Current formData.category1:", formData.category1);
    
    // Validate category1 field in step 1
    if (step === 0 && !formData.category1) {
      console.log("❌ Category1 validation failed - field is empty");
      alert("❌ Please select a category1.");
      return;
    }
    
    if (abstractsError && step === 1) {
      alert("❌ Please ensure each abstract has a different Presenting Author.");
      return;
    }
    
    if (step < steps.length - 1) {
      console.log("✅ Validation passed, saving and moving to next step...");
      // Always save when moving to next step
      await updateUserInfo(formData);
      setStep(prev => prev + 1);
    } else {
      setShowConfirmation(true);
    }
  };

  const prevStep = () => {
    if (step > 0) {
      console.log("🔄 Going back to previous step:", step - 1);
      setStep(prev => prev - 1);
    }
  };

  // Remove the tab click function entirely - no more jumping between tabs
  // const handleTabClick = async (tabIndex) => { ... };

  const updateFormData = (newData) => {
    setFormData(prev => ({ ...prev, ...newData }));
    // Mark that there are unsaved changes
  };

  // Force save current form data
  const forceSave = async () => {
    console.log("💾 Force saving current form data...");
    await updateUserInfo(formData);
  };

  // Remove the saveAndContinue function - no longer needed

  // Remove the problematic beforeunload event listener

  const handleSubmitFinalForm = async () => {
    try {
      const storedUid = sessionStorage.getItem("uid");
      if (!storedUid) {
        console.error("User ID missing. Cannot submit.");
        return;
      }
      await updateUserInfo(formData);
      alert("✅ Registration Form Saved Successfully!");
      // Don't navigate away - stay in the registration form for payment
      // navigate("/stis2025/payment");
    } catch (error) {
      console.error("❌ Error submitting final form:", error.message);
      alert("❌ Submission failed. Please try again.");
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <Navbar />
      <div className="registration-form-container">
        {/* Registration Pending Banner */}
        {!loadingPayments && userPayments && !userPayments.some(payment => payment.status === 'captured' || payment.status === 'paid') && (
          <div style={{
            backgroundColor: '#dc2626',
            color: 'white',
            textAlign: 'center',
            padding: '12px 20px',
            fontSize: '16px',
            fontWeight: 'bold',
            borderBottom: '3px solid #b91c1c',
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            marginBottom: '20px',
            borderRadius: '8px'
          }}>
            ⚠️ Registration Pending - Please Complete the Payment Process to Finalize Your Registration
          </div>
        )}
        
        <div className="step-indicator">
          {steps.map((s, idx) => (
            <div 
              key={idx} 
              className={`step ${idx === step ? "active" : ""} ${idx <= step ? "completed" : ""}`}
              style={{ cursor: 'default' }}
            >
              {s}
            </div>
          ))}
        </div>

        {/* Auto-save indicator */}
        {isSaving && (
          <div style={{
            backgroundColor: '#10b981',
            color: 'white',
            textAlign: 'center',
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: 'bold',
            borderRadius: '4px',
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid white',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            Auto-saving...
          </div>
        )}

        {/* Remove the unsaved changes indicator - simplifying the interface */}

        <div className="form-content">
          {step === 0 && (
            <Step1PersonalDetails
              formData={formData}
              updateFormData={updateFormData}
            />
          )}
          {step === 1 && (
            <Step2AbstractDetails
              formData={formData}
              updateFormData={updateFormData}
              setAbstractsError={setAbstractsError}
            />
          )}
          {step === 2 && (
            <Step3AccompanyingDetails
              formData={formData}
              updateFormData={updateFormData}
            />
          )}
          {step === 3 && (
            <Step4PaymentSelection
              formData={formData}
              updateFormData={updateFormData}
            />
          )}
        </div>

        <div className="form-navigation">
          {step > 0 && (
            <button className="nav-button" onClick={prevStep}>
              Previous
            </button>
          )}
          
          {/* Manual Save Button */}
          <button 
            className="nav-button" 
            onClick={forceSave}
            disabled={isSaving}
            style={{ 
              backgroundColor: isSaving ? '#ccc' : '#28a745',
              marginLeft: '10px'
            }}
          >
            {isSaving ? 'Saving...' : 'Save Progress'}
          </button>
          
          {step < steps.length - 1 ? (
            <button className="nav-button" onClick={nextStep}>
              Next
            </button>
          ) : (
            showConfirmation && (
              <button className="nav-button" onClick={handleSubmitFinalForm}>
                Final Submit
              </button>
            )
          )}
        </div>
      </div>
      <br />
      <br />
      <Footer />
    </>
  );
};

export default RegistrationForm;
